import React, { useCallback, useEffect, useState } from 'react';
import WorkoutLogger from './WorkoutLogger';
import WorkoutSummary from './WorkoutSummary';
import InsightsPanel from './InsightsPanel';
import { errorText } from './strengthFormat';

function TrainView({
  api, t, lang, showMessage, catalog, catalogByKey, plans, stravaStatus,
  onPlansChanged, onStravaChanged, onGoToPlans, onGoToStrava, onGoToProgress
}) {
  // undefined while loading, null when nothing is in progress
  const [session, setSession] = useState(undefined);
  const [finished, setFinished] = useState(null);
  const [starting, setStarting] = useState(false);
  const [chosenDay, setChosenDay] = useState({});

  const loadActive = useCallback(async () => {
    try {
      setSession(await api.activeSession());
    } catch (e) {
      setSession(null);
      showMessage(errorText(e, t('strength.errors.load')), 'error');
    }
  }, [api, showMessage, t]);

  useEffect(() => { loadActive(); }, [loadActive]);

  const start = async (planId, planDayId) => {
    setStarting(true);
    try {
      setFinished(null);
      setSession(await api.startSession({ planId, planDayId }));
    } catch (e) {
      showMessage(errorText(e, t('strength.errors.start')), 'error');
      await loadActive();
    } finally {
      setStarting(false);
    }
  };

  if (session === undefined) return <div className="st-empty">{t('strength.loading')}</div>;

  if (session) {
    const plan = plans.find(p => p.id === session.workoutPlanId) || null;
    return (
      <WorkoutLogger
        api={api}
        t={t}
        lang={lang}
        session={session}
        plan={plan}
        catalog={catalog}
        catalogByKey={catalogByKey}
        showMessage={showMessage}
        onFinished={(result) => {
          setFinished(result);
          setSession(null);
          onPlansChanged();
          onStravaChanged();
        }}
        onDiscarded={() => setSession(null)}
      />
    );
  }

  if (finished) {
    return (
      <WorkoutSummary
        api={api}
        t={t}
        lang={lang}
        result={finished}
        stravaStatus={stravaStatus}
        onDone={() => setFinished(null)}
        onGoToStrava={onGoToStrava}
      />
    );
  }

  const myPlans = plans.filter(p => p.isMine);
  const householdPlans = plans.filter(p => !p.isMine);

  const planCard = (plan) => {
    const dayId = chosenDay[plan.id] || plan.nextDayId || plan.days[0]?.id;
    const day = plan.days.find(d => d.id === dayId) || plan.days[0];
    if (!day) return null;
    return (
      <article key={plan.id} className="st-card st-next-card">
        <div className="st-card-head">
          <div>
            <span className="st-eyebrow">{plan.isMine ? t('strength.train.upNext') : plan.ownerName}</span>
            <h3>{day.name}</h3>
            <p className="st-muted">{plan.name}</p>
          </div>
          {plan.isAiGenerated && <span className="st-badge st-badge-ai">✨ AI</span>}
        </div>
        <p className="st-exercise-preview">
          {day.exercises.slice(0, 5).map(e => e.exerciseName).join(' · ')}
          {day.exercises.length > 5 && ` · +${day.exercises.length - 5}`}
        </p>
        <div className="st-row">
          {plan.days.length > 1 && (
            <select
              className="st-select"
              value={day.id}
              onChange={(e) => setChosenDay(prev => ({ ...prev, [plan.id]: e.target.value }))}
              aria-label={t('strength.train.chooseDay')}
            >
              {plan.days.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
          <button className="st-btn st-btn-primary" disabled={starting} onClick={() => start(plan.id, day.id)}>
            ▶ {t('strength.train.start')}
          </button>
        </div>
      </article>
    );
  };

  return (
    <div className="st-train">
      {stravaStatus?.configured && !stravaStatus.connected && (
        <button className="st-callout st-callout-strava" onClick={onGoToStrava}>
          <span>🔗 {t('strength.train.connectHint')}</span>
          <span aria-hidden="true">→</span>
        </button>
      )}

      {myPlans.length === 0 ? (
        <div className="st-card st-welcome">
          <h3>{t('strength.train.welcomeTitle')}</h3>
          <p>{t('strength.train.welcomeBody')}</p>
          <div className="st-row">
            <button className="st-btn st-btn-primary" onClick={onGoToPlans}>✨ {t('strength.train.buildWithAi')}</button>
            <button className="st-btn" disabled={starting} onClick={() => start(null, null)}>{t('strength.train.emptyWorkout')}</button>
          </div>
        </div>
      ) : (
        <>
          <div className="st-grid">{myPlans.map(planCard)}</div>
          <button className="st-btn st-btn-ghost" disabled={starting} onClick={() => start(null, null)}>
            ＋ {t('strength.train.emptyWorkout')}
          </button>
        </>
      )}

      {householdPlans.length > 0 && (
        <section className="st-section">
          <h2 className="st-section-title">{t('strength.train.householdPlans')}</h2>
          <div className="st-grid">{householdPlans.map(planCard)}</div>
        </section>
      )}

      <section className="st-section">
        <InsightsPanel api={api} t={t} lang={lang} showMessage={showMessage} compact onOpenProgress={onGoToProgress} />
      </section>
    </div>
  );
}

export default TrainView;
